/**
 * Phoenix Template Preview Adapter
 *
 * Transforms Phoenix-format WhatsApp template data into the format expected by
 * the automation-studio template preview iframe.
 *
 * Phoenix templates use lowercase component types and a flat parameter structure,
 * while the preview expects UPPERCASE types and a structured `links` object.
 *
 * @module phoenixTemplatePreviewAdapter
 */

const MEDIA_PARAM_TYPES = ['image', 'video', 'document'];

const getMediaValue = (media) => {
  if (!media) return '';
  if (typeof media === 'string') return media;
  if (typeof media.link === 'string') return media.link;
  if (typeof media.id === 'string') return media.id;
  return '';
};

const extractParameterValue = (parameter = {}) => {
  const type = (parameter.type || '').toLowerCase();

  if (type === 'text') {
    return parameter.text || '';
  }

  if (type === 'payload') {
    return parameter.payload || '';
  }

  if (type === 'currency') {
    if (typeof parameter.currency === 'object' && parameter.currency) {
      return (
        parameter.currency.fallback_value ||
        parameter.currency.code ||
        parameter.currency.amount ||
        ''
      );
    }
    return parameter.currency || '';
  }

  if (type === 'date_time') {
    if (typeof parameter.date_time === 'object' && parameter.date_time) {
      return parameter.date_time.fallback_value || '';
    }
    return parameter.date_time || '';
  }

  if (MEDIA_PARAM_TYPES.includes(type)) {
    const mediaSource =
      parameter[type] ||
      parameter.media ||
      parameter.image ||
      parameter.video ||
      parameter.document;

    const mediaValue = getMediaValue(mediaSource);
    if (mediaValue) return mediaValue;

    if (parameter.link) return parameter.link;
    if (parameter.id) return parameter.id;
    return '';
  }

  if (parameter.text) {
    return parameter.text;
  }

  return '';
};

const normalizeParameter = (parameter = {}) => {
  const type = (parameter.type || '').toLowerCase();
  const normalized = {
    ...parameter,
    type,
  };

  const value = extractParameterValue(parameter);
  if (value !== '') {
    normalized.value = value;
  }

  return normalized;
};

const normalizeButton = (button = {}) => ({
  ...button,
  type: (button.type || '').toLowerCase(),
});

const normalizeComponent = (component = {}) => {
  const normalized = {
    ...component,
    type: (component.type || '').toLowerCase(),
  };

  if (Array.isArray(component.parameters)) {
    normalized.parameters = component.parameters.map(normalizeParameter);
  }

  if (Array.isArray(component.buttons)) {
    normalized.buttons = component.buttons.map(normalizeButton);
  }

  if (typeof normalized.sub_type === 'string') {
    normalized.sub_type = normalized.sub_type.toLowerCase();
  }

  if (typeof normalized.subType === 'string') {
    normalized.subType = normalized.subType.toLowerCase();
  }

  return normalized;
};

const extractMediaFromComponents = (components = []) => {
  for (const component of components) {
    if (component.type !== 'header' || !Array.isArray(component.parameters)) {
      continue;
    }

    const mediaParam = component.parameters.find((param) =>
      MEDIA_PARAM_TYPES.includes(param.type)
    );

    if (mediaParam?.value) {
      return mediaParam.value;
    }
  }

  return '';
};

const collectParameterValuesFromComponents = (components = []) => {
  return components.reduce((values, component) => {
    const componentType = (component.type || '').toLowerCase();

    // Header parameter values (media IDs, etc.) must not be mixed into the
    // positional body/button values — they are handled separately via
    // extractMediaFromComponents and the media URL on templateParams.
    if (componentType === 'header') {
      return values;
    }

    if (Array.isArray(component.parameters)) {
      component.parameters
        .map(normalizeParameter)
        .forEach((parameter) => {
          const candidate =
            parameter.value ??
            parameter.text ??
            parameter.payload ??
            '';

          if (candidate !== '') {
            values.push(String(candidate));
          }
        });
    }

    if (Array.isArray(component.buttons)) {
      component.buttons.forEach((button) => {
        if (Array.isArray(button.parameters)) {
          button.parameters
            .map(normalizeParameter)
            .forEach((parameter) => {
              const candidate =
                parameter.value ??
                parameter.text ??
                parameter.payload ??
                '';

              if (candidate !== '') {
                values.push(String(candidate));
              }
            });
        }
      });
    }

    return values;
  }, []);
};

export const normalizePhoenixComponents = (components = []) =>
  components.map((component) => normalizeComponent(component || {}));

/**
 * Builds a preview payload from Phoenix template parameters and components.
 *
 * Extracts parameter values and transforms them into the format expected by
 * the automation-studio template preview iframe.
 *
 * @param {Object} templateParams - Template parameters from the message (may contain components array)
 * @param {Array} templateComponents - Template component definitions from the fetched template
 * @returns {Object|null} Preview payload with header, body, buttons arrays, or null if no valid components
 */
export const buildPhoenixPreviewPayload = (
  templateParams = {},
  templateComponents = []
) => {
  const fetchedComponents = Array.isArray(templateComponents)
    ? templateComponents
    : [];

  const messageComponents = Array.isArray(templateParams.components)
    ? templateParams.components
    : [];

  const componentsSource = fetchedComponents.length > 0
    ? fetchedComponents
    : messageComponents;

  if (componentsSource.length === 0) {
    return null;
  }

  const normalizedMessageComponents = messageComponents.map(normalizeComponent);

  let paramValues = collectParameterValuesFromComponents(
    normalizedMessageComponents
  );

  const processedParams = templateParams.processed_params || {};
  if (paramValues.length === 0) {
    paramValues = Array.isArray(processedParams)
      ? processedParams.map((p) =>
          typeof p === 'string' ? p : p?.text || p?.value || String(p)
        )
      : Object.values(processedParams);
  }

  if (paramValues.length === 0) {
    paramValues = [];

    for (const component of componentsSource) {
      const componentType = (component?.type || '').toLowerCase();

      if (componentType === 'header' && component?.example?.header_handle) {
        const handles = component.example.header_handle;
        if (Array.isArray(handles)) {
          paramValues.push(...handles);
        }
      }

      if (componentType === 'body' && component?.example?.body_text) {
        const bodyTexts = component.example.body_text;
        if (Array.isArray(bodyTexts)) {
          for (const text of bodyTexts) {
            if (Array.isArray(text)) {
              paramValues.push(...text);
            } else {
              paramValues.push(text);
            }
          }
        }
      }

      if ((componentType === 'buttons' || componentType === 'button') && Array.isArray(component?.buttons)) {
        for (const button of component.buttons) {
          if (button?.example && Array.isArray(button.example)) {
            paramValues.push(...button.example);
          }
        }
      }

      if (Array.isArray(component?.parameters)) {
        for (const param of component.parameters) {
          if (param?.text) {
            paramValues.push(param.text);
          } else if (param?.example) {
            if (Array.isArray(param.example)) {
              paramValues.push(...param.example);
            } else if (typeof param.example === 'string') {
              paramValues.push(param.example);
            }
          }
        }
      }
    }
  }

  const components = componentsSource.map((component) =>
    normalizeComponent(component || {})
  );

  let paramIndex = 0;

  const sentParams = components.reduce((accumulator, component) => {
    const componentType = component.type;
    const hasParameters = Array.isArray(component.parameters) && component.parameters.length;
    const paramType = hasParameters
      ? component.parameters[0]?.type?.toLowerCase()
      : undefined;

    if (componentType === 'header') {
      if (hasParameters) {
        // Use the component's own parameter value directly — header values
        // are intentionally excluded from the positional paramValues array
        // to prevent media IDs from leaking into body placeholders.
        const value =
          component.parameters[0]?.value ??
          component.parameters[0]?.text ??
          component.parameters[0]?.payload ??
          '';

        if (paramType === 'image') {
          accumulator.header = [{
            type: 'image',
            image: { link: value },
          }];
        } else if (paramType === 'video') {
          accumulator.header = [{
            type: 'video',
            video: { link: value },
          }];
        } else {
          accumulator.header = [{
            type: 'text',
            text: String(value),
          }];
        }
      } else if (component.text) {
        accumulator.header = [{
          type: 'text',
          text: component.text,
        }];
      }
      return accumulator;
    }

    if (componentType === 'body') {
      let paramCount = 0;
      if (Array.isArray(component.parameters)) {
        paramCount = component.parameters.length;
      } else if (component.text) {
        const matches = component.text.match(/\{\{(\d+)\}\}/g);
        paramCount = matches ? matches.length : 0;
      }

      accumulator.body = Array.from({ length: paramCount }, () => {
        const value = paramValues[paramIndex] || '';
        paramIndex++;
        return {
          type: 'text',
          text: String(value),
        };
      });
      return accumulator;
    }

    if (componentType === 'button' || componentType === 'buttons') {
      if (!accumulator.buttons) {
        accumulator.buttons = [];
      }

      if (Array.isArray(component.parameters) && component.parameters.length) {
        component.parameters.forEach(() => {
          const value = paramValues[paramIndex] || '';
          paramIndex++;
          accumulator.buttons.push({
            type: 'text',
            text: String(value),
          });
        });
        return accumulator;
      }

      if (Array.isArray(component.buttons)) {
        component.buttons.forEach((button) => {
          if (Array.isArray(button.parameters) && button.parameters.length) {
            button.parameters.forEach(() => {
              const value = paramValues[paramIndex] || '';
              paramIndex++;
              accumulator.buttons.push({
                type: 'text',
                text: String(value),
              });
            });
            return;
          }

          if (button.url && button.url.includes('{{')) {
            const value = paramValues[paramIndex] || '';
            paramIndex++;
            accumulator.buttons.push({
              type: 'text',
              text: String(value),
            });
          }
        });
      }

      return accumulator;
    }

    return accumulator;
  }, {});

  const media =
    templateParams.media ||
    extractMediaFromComponents(normalizedMessageComponents) ||
    extractMediaFromComponents(components) ||
    undefined;

  const previewPayload = {
    ...sentParams,
  };

  if (media) {
    previewPayload.media = media;
  }

  return previewPayload;
};

const buildLinkEntries = (values = []) =>
  values
    .map((value, index) => {
      const resolvedValue = value ?? '';
      if (resolvedValue === '') {
        return null;
      }

      return {
        number: index + 1,
        value: String(resolvedValue),
        example: '',
      };
    })
    .filter(Boolean);

export const normalizePhoenixTranslation = (translation, sentParams = null) => {
  if (!translation?.components) {
    return translation;
  }

  const headerTextFromParams = (sentParams?.header || []).find(
    (param) =>
      param &&
      String(param.type || '').toLowerCase() === 'text' &&
      param.text
  )?.text;

  const normalizedComponents = translation.components.map((component = {}) => {
    const normalizedType = (component.type || '').toUpperCase();
    const normalized = {
      ...component,
      type: normalizedType,
    };

    if (normalizedType === 'HEADER') {
      if (typeof component.format === 'string') {
        normalized.format = component.format.toUpperCase();
      } else if (headerTextFromParams || component.text) {
        normalized.format = 'TEXT';
      }

      if (headerTextFromParams) {
        normalized.text = headerTextFromParams;
      }
    }

    if (normalizedType === 'BUTTONS' && Array.isArray(component.buttons)) {
      normalized.buttons = component.buttons.map((button) => ({
        ...button,
        type: (button.type || '').toUpperCase(),
      }));
    }

    return normalized;
  });

  const normalized = {
    ...translation,
    components: normalizedComponents,
  };

  const links = { ...(translation.links || {}) };

  const bodyLinkValues = (sentParams?.body || []).map((param) => param?.text ?? param?.value ?? '');
  const buttonLinkValues = (sentParams?.buttons || []).map((param) => param?.text ?? param?.value ?? '');

  const headerLinkValue =
    headerTextFromParams ||
    (sentParams?.header || []).find((param) => param && param.text)?.text ||
    '';

  const headerLinks = buildLinkEntries(headerLinkValue ? [headerLinkValue] : []);
  const bodyLinks = buildLinkEntries(bodyLinkValues);
  const buttonLinks = buildLinkEntries(buttonLinkValues);

  if (headerLinks.length) {
    links.header = headerLinks;
  } else if (links.header) {
    delete links.header;
  }

  if (bodyLinks.length) {
    links.body = bodyLinks;
  } else if (links.body) {
    delete links.body;
  }

  if (buttonLinks.length) {
    links.buttons = buttonLinks;
  } else if (links.buttons) {
    delete links.buttons;
  }

  normalized.links = links;

  return normalized;
};
